import { useState, useMemo, useEffect } from 'react'
import useDealStore from '../store/dealStore'
import '../styles/module15.css'

const money   = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`
const pct     = (v) => `${(Number(v) || 0).toFixed(1)}%`
const today   = () => new Date().toISOString().split('T')[0]
const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'

// ── Value-add categories ──────────────────────────────────────────────────────
const VALUE_ADD_ITEMS = [
  { id: 'interior_reno',  label: 'Interior Unit Renovations',  icon: '🛋️', costPer: 8500,  rentBump: 175 },
  { id: 'exterior_paint', label: 'Exterior Paint & Curb',      icon: '🎨', costPer: 3200,  rentBump: 50  },
  { id: 'laundry',        label: 'In-Unit Washer/Dryer',       icon: '🫧', costPer: 1800,  rentBump: 100 },
  { id: 'hvac',           label: 'HVAC Upgrades',              icon: '❄️', costPer: 5500,  rentBump: 60  },
  { id: 'landscaping',    label: 'Landscaping & Common Areas', icon: '🌿', costPer: 2200,  rentBump: 35  },
  { id: 'security',       label: 'Security Cameras & Access',  icon: '📷', costPer: 1500,  rentBump: 40  },
  { id: 'parking',        label: 'Parking Lot Resurfacing',    icon: '🚗', costPer: 1800,  rentBump: 30  },
  { id: 'solar',          label: 'Solar / EV Charging',        icon: '☀️', costPer: 12000, rentBump: 80  },
]

const PM_SOFTWARE = ['AppFolio', 'Buildium', 'Yardi', 'RentRedi', 'DoorLoop', 'Propertyware', 'Other']

function KpiGauge({ label, value, target, unit, color }) {
  const pctFill = Math.min((value / (target || 1)) * 100, 100)
  return (
    <div className="m15-kpi-card">
      <div className="m15-kpi-label">{label}</div>
      <div className="m15-kpi-value" style={{ color }}>{unit === '$' ? money(value) : unit === '%' ? pct(value) : value + (unit || '')}</div>
      <div className="m15-kpi-bar-wrap">
        <div className="m15-kpi-bar" style={{ width: `${pctFill}%`, backgroundColor: color }} />
      </div>
      <div className="m15-kpi-target">Target: {unit === '$' ? money(target) : unit === '%' ? pct(target) : target + (unit || '')}</div>
    </div>
  )
}

function MiniSparkline({ data, color }) {
  if (!data || data.length < 2) return null
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const w = 120, h = 32
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w
    const y = h - ((v - min) / range) * h
    return `${x},${y}`
  }).join(' ')
  return (
    <svg width={w} height={h} className="m15-sparkline">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  )
}

export default function Module15Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()

  const raw    = dealState.rawDealData          || {}
  const fin    = dealState.extractedFinancials  || {}
  const uw     = dealState.underwritingMetrics  || {}
  const score  = dealState.scoreAndOffer        || {}
  const closing = dealState.closingDetails      || {}
  const saved  = dealState.assetManagement      || {}
  const ownershipCosts = dealState.ownershipCosts || {}

  const propName = raw.location?.address || 'Subject Property'
  const location = raw.location?.city && raw.location?.state ? `${raw.location.city}, ${raw.location.state}` : ''
  const units    = Number(raw.property?.units) || 0
  const price    = Number(raw.price) || 0
  const noi      = Number(fin.noi) || 0
  const revenue  = Number(fin.revenue?.totalRevenue) || 0
  const capRate  = Number(uw.capRate?.entry) || (price > 0 ? (noi / price) * 100 : 0)
  const dscr     = Number(uw.dscr?.current) || 0
  const occupancy = Number(fin.revenue?.rentRoll?.occupancyRate) || 93
  const avgRent  = units > 0 ? Math.round(revenue / 12 / units) : 0
  const closingDate = closing.closingDate || today()

  // KPI targets derived from underwriting
  const targetOccupancy = 95
  const targetRent = Math.round(avgRent * 1.08)
  const targetNOI  = Math.round(noi * 1.10)
  const targetDSCR = Math.max(dscr, 1.25)

  // Restore saved
  const savedKPIs     = saved.kpiDashboard      || {}
  const savedMgmt     = saved.managementPlan    || {}
  const savedPerf     = saved.performanceTracking || {}
  const savedValueAdd = saved.valueAdd          || {}
  const savedHold     = saved.holdStrategy      || {}

  const [activeTab, setActiveTab]     = useState('kpi')
  const [kpiOccupancy, setKpiOccupancy] = useState(savedKPIs.occupancy   || occupancy)
  const [kpiRent,      setKpiRent]      = useState(savedKPIs.rent         || avgRent)
  const [kpiNOI,       setKpiNOI]       = useState(savedKPIs.noi          || noi)
  const [kpiDSCR,      setKpiDSCR]      = useState(savedKPIs.dscr         || dscr)
  const [kpiCapRate,   setKpiCapRate]   = useState(savedKPIs.capRate      || capRate.toFixed(2))

  // Management plan
  const [pmCompany,  setPmCompany]  = useState(savedMgmt.pmCompany  || '')
  const [pmFee,      setPmFee]      = useState(savedMgmt.pmFee      || '8')
  const [pmSoftware, setPmSoftware] = useState(savedMgmt.pmSoftware || 'AppFolio')
  const [pmContact,  setPmContact]  = useState(savedMgmt.pmContact  || '')
  const [pmNotes,    setPmNotes]    = useState(savedMgmt.pmNotes    || '')

  // Value-add
  const [vaSelected,  setVaSelected]  = useState(savedValueAdd.selected   || [])
  const [vaUnits,     setVaUnits]     = useState(savedValueAdd.unitsCount  || {})
  const [vaCompleted, setVaCompleted] = useState(savedValueAdd.completed   || [])

  // Hold strategy
  const [holdYears,    setHoldYears]    = useState(savedHold.years      || '5')
  const [exitCapRate,  setExitCapRate]  = useState(savedHold.exitCapRate || (capRate * 0.9).toFixed(2))
  const [exitStrategy, setExitStrategy] = useState(savedHold.strategy   || 'sell')
  const [refinanceYr,  setRefinanceYr]  = useState(savedHold.refinanceYear || '3')
  const [holdNotes,    setHoldNotes]    = useState(savedHold.notes      || '')

  // Monthly perf tracker (12 months simulated from base)
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const baseMonthlyNOI = Math.round(noi / 12)
  const [perfData, setPerfData] = useState(
    savedPerf.monthly ||
    MONTHS.map((m, i) => ({
      month: m,
      occupancy: Math.min(98, Math.round(occupancy + (i < 3 ? -1 : i < 6 ? 0 : i < 9 ? 1 : 2))),
      rent:      Math.round(avgRent + i * 5),
      noi:       Math.round(baseMonthlyNOI + i * 200 - 300),
      entered:   false
    }))
  )

  const [saving,    setSaving]    = useState(false)
  const [activated, setActivated] = useState(!!savedKPIs.activatedAt)

  // ── Expense Ratio Monitor ─────────────────────────────────────────────
  const ITEM_DURATION_MONTHS = { interior_reno: 3, exterior_paint: 1, laundry: 1, hvac: 2, landscaping: 1, security: 1, parking: 1, solar: 3 }

  const [expItems, setExpItems] = useState(() => {
    const base = revenue / 12 || 1
    return [
      { id: 'taxes',      cat: 'Taxes & Insurance', label: 'Property Taxes',      model: ownershipCosts.annualPropertyTax || Math.round(revenue * 0.10), actual: ownershipCosts.annualPropertyTax || Math.round(revenue * 0.10) },
      { id: 'insurance',  cat: 'Taxes & Insurance', label: ownershipCosts.insuranceIsEstimated ? 'Insurance (est.)' : 'Insurance', model: ownershipCosts.annualInsurance || Math.round(revenue * 0.04), actual: ownershipCosts.annualInsurance || Math.round(revenue * 0.04) },
      { id: 'maintenance',cat: 'Operations',        label: 'Maintenance & Repairs',model: Math.round(revenue * 0.05), actual: Math.round(revenue * 0.05) },
      { id: 'management', cat: 'Operations',        label: 'Property Management', model: Math.round(revenue * 0.08), actual: Math.round(revenue * 0.08) },
      { id: 'utilities',  cat: 'Operations',        label: 'Utilities',           model: Math.round(revenue * 0.04), actual: Math.round(revenue * 0.04) },
      { id: 'admin',      cat: 'Admin',             label: 'Admin & Legal',       model: Math.round(revenue * 0.015),actual: Math.round(revenue * 0.015) },
      { id: 'reserves',   cat: 'Reserves',          label: 'Capital Reserves',    model: Math.round(revenue * 0.05), actual: Math.round(revenue * 0.05) },
      { id: 'other',      cat: 'Other',             label: 'Other Expenses',      model: 0,                          actual: 0 },
    ]
  })
  const [vaStartDates, setVaStartDates] = useState({})

  // Value-add calcs
  const vaTotalCost = useMemo(() => {
    return vaSelected.reduce((sum, id) => {
      const item = VALUE_ADD_ITEMS.find(v => v.id === id)
      const n    = Number(vaUnits[id] || units)
      return sum + (item?.costPer || 0) * n
    }, 0)
  }, [vaSelected, vaUnits, units])

  const vaRentBump = useMemo(() => {
    return vaSelected.reduce((sum, id) => {
      const item = VALUE_ADD_ITEMS.find(v => v.id === id)
      return sum + (item?.rentBump || 0)
    }, 0)
  }, [vaSelected])

  const vaNewNOI  = noi + (vaRentBump * units * 12 * 0.85)
  const vaROI     = vaTotalCost > 0 ? ((vaRentBump * units * 12) / vaTotalCost) * 100 : 0
  const vaNewValue= exitCapRate > 0 ? vaNewNOI / (Number(exitCapRate) / 100) : 0

  // Exit projections
  const holdPeriod = Number(holdYears) || 5
  const projectedNOI   = noi * Math.pow(1.03, holdPeriod)   // 3% growth/yr
  const projectedValue = projectedNOI / (Number(exitCapRate || capRate) / 100)
  const loanBalance    = Number(uw.debtService?.loanAmount || price * 0.75) * Math.pow(0.97, holdPeriod)
  const projectedEquity = projectedValue - loanBalance
  const projectedIRR   = price > 0
    ? (Math.pow(projectedValue / price, 1 / holdPeriod) - 1) * 100
    : 0

  // Auto-persist all asset plan selections on every change
  useEffect(() => {
    updateDealState({
      assetManagement: {
        ...(dealState.assetManagement || {}),
        kpiDashboard: { occupancy: kpiOccupancy, rent: kpiRent, noi: kpiNOI, dscr: kpiDSCR, capRate: kpiCapRate },
        managementPlan: { pmCompany, pmFee, pmSoftware, pmContact, pmNotes },
        valueAdd: { selected: vaSelected, unitsCount: vaUnits, completed: vaCompleted },
        holdStrategy: { years: holdYears, exitCapRate, strategy: exitStrategy, refinanceYear: refinanceYr, notes: holdNotes },
      }
    })
  }, [kpiOccupancy, kpiRent, kpiNOI, kpiDSCR, kpiCapRate, // eslint-disable-line
      pmCompany, pmFee, pmSoftware, pmContact, pmNotes,
      vaSelected, vaUnits, vaCompleted,
      holdYears, exitCapRate, exitStrategy, refinanceYr, holdNotes])

  function toggleVA(id) {
    setVaSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }
  function setVaUnit(id, val) { setVaUnits(prev => ({ ...prev, [id]: val })) }
  function toggleVaComplete(id) { setVaCompleted(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]) }

  function updatePerf(i, field, val) {
    setPerfData(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: val, entered: true } : row))
  }

  function buildPayload(activating = false) {
    return {
      kpiDashboard: {
        occupancy: kpiOccupancy,
        rent:      kpiRent,
        noi:       kpiNOI,
        dscr:      kpiDSCR,
        capRate:   kpiCapRate,
        performanceVsTarget: {
          occupancy: ((kpiOccupancy / targetOccupancy) * 100).toFixed(1),
          noi:       ((kpiNOI / targetNOI) * 100).toFixed(1),
        },
        activatedAt: activating ? new Date().toISOString() : (savedKPIs.activatedAt || null)
      },
      managementPlan: { pmCompany, pmFee, pmSoftware, pmContact, pmNotes },
      valueAdd: {
        selected: vaSelected,
        unitsCount: vaUnits,
        completed: vaCompleted,
        totalCost: vaTotalCost,
        rentBump:  vaRentBump,
        newNOI:    vaNewNOI,
        roi:       vaROI,
      },
      holdStrategy: {
        years: holdYears,
        exitCapRate,
        strategy: exitStrategy,
        refinanceYear: refinanceYr,
        notes: holdNotes,
        projectedValue,
        projectedEquity,
        projectedIRR,
      },
      performanceTracking: { monthly: perfData },
    }
  }

  function handleSave() {
    updateDealState({ assetManagement: buildPayload(false) })
  }

  function handleActivate() {
    setSaving(true)
    updateDealState({
      assetManagement: buildPayload(true),
      pipelineStage:   Math.max(dealState.pipelineStage || 1, 16),
      pipelineStatus:  'in_progress'
    })
    updateAvailableModules()
    setActivated(true)
    setTimeout(() => {
      setSaving(false)
      forceNavigateToModule(17)
    }, 900)
  }

  if (activated && !saving) {
    return (
      <div className="m15-complete-screen">
        <div className="m15-complete-card">
          <div className="m15-complete-icon">📊</div>
          <h2>Asset Dashboard Activated</h2>
          <p>KPIs set. Management plan configured. Advancing to Exit Strategy.</p>
          <div className="m15-complete-badge">Module 15 Complete</div>
          <button className="m15-proceed-btn" onClick={() => forceNavigateToModule(17)}>Go to Exit Strategy</button>
        </div>
      </div>
    )
  }

  return (
    <div className="m15-page">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="m15-header">
        <div className="m15-header-left">
          <div className="m15-module-badge">Module 15</div>
          <div>
            <h1>Asset Plan &amp; Hold</h1>
            <p className="m15-sub">{propName}{location && ` · ${location}`} · {units} units · Acquired {fmtDate(closingDate)}</p>
          </div>
        </div>
        <div className="m15-header-right">
          <div className="m15-hold-pill">
            <span className="m15-pill-label">Hold</span>
            <span className="m15-pill-val">{holdYears}yr</span>
          </div>
          <button className="m15-save-btn" onClick={handleSave}>Save Progress</button>
        </div>
      </div>

      {/* ── Portfolio strip ────────────────────────────────────── */}
      <div className="m15-strip">
        {[
          { label: 'Purchase Price', val: money(price)               },
          { label: 'Units',          val: units || '—'               },
          { label: 'Current NOI',    val: money(kpiNOI)              },
          { label: 'Cap Rate',       val: pct(kpiCapRate)            },
          { label: 'DSCR',           val: (Number(kpiDSCR)||0).toFixed(2) + 'x' },
          { label: 'Occupancy',      val: pct(kpiOccupancy)          },
          ...(ownershipCosts.totalMonthlyOwnershipCost > 0 ? [{ label: 'Total Monthly Cost', val: money(ownershipCosts.totalMonthlyOwnershipCost) }] : []),
          ...(ownershipCosts.annualInsurance > 0 ? [{ label: `Insurance${ownershipCosts.insuranceIsEstimated ? ' (est.)' : ''}/yr`, val: money(ownershipCosts.annualInsurance) }] : []),
        ].map(s => (
          <div key={s.label} className="m15-strip-item">
            <div className="m15-strip-val">{s.val}</div>
            <div className="m15-strip-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ───────────────────────────────────────────────── */}
      <div className="m15-tabs">
        {[
          { id: 'kpi',        label: '📊 KPI Dashboard' },
          { id: 'management', label: '🏢 Management Plan' },
          { id: 'valueadd',   label: '🔨 Value-Add Plan' },
          { id: 'performance',label: '📈 Performance Tracking' },
          { id: 'hold',       label: '🎯 Hold Strategy' },
          { id: 'expense',    label: '📉 Expense Monitor' },
        ].map(t => (
          <button key={t.id} className={`m15-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="m15-content">

        {/* ── KPI DASHBOARD ──────────────────────────────────── */}
        {activeTab === 'kpi' && (
          <div className="m15-kpi-panel">
            <div className="m15-section-header">
              <h3>KPI Dashboard</h3>
              <p>Set your baseline metrics at acquisition. These become your performance benchmarks.</p>
            </div>

            <div className="m15-kpi-grid">
              <KpiGauge label="Occupancy Rate"  value={kpiOccupancy} target={targetOccupancy} unit="%" color="#1d4ed8" />
              <KpiGauge label="Avg Rent / Unit" value={kpiRent}      target={targetRent}      unit="$" color="#0284c7" />
              <KpiGauge label="Annual NOI"      value={kpiNOI}       target={targetNOI}       unit="$" color="#2563eb" />
              <KpiGauge label="DSCR"            value={kpiDSCR}      target={targetDSCR}      unit="x" color="#1e3a8a" />
              <KpiGauge label="Cap Rate"        value={kpiCapRate}   target={capRate * 1.05}  unit="%" color="#0f2d5e" />
            </div>

            <div className="m15-kpi-inputs">
              <h4>Edit Current KPIs</h4>
              <div className="m15-kpi-form">
                {[
                  { label: 'Occupancy %',      val: kpiOccupancy, set: setKpiOccupancy, step: '0.1' },
                  { label: 'Avg Rent/Unit $',  val: kpiRent,      set: setKpiRent,      step: '1'   },
                  { label: 'Annual NOI $',     val: kpiNOI,       set: setKpiNOI,       step: '100' },
                  { label: 'DSCR',             val: kpiDSCR,      set: setKpiDSCR,      step: '0.01'},
                  { label: 'Cap Rate %',       val: kpiCapRate,   set: setKpiCapRate,   step: '0.1' },
                ].map(f => (
                  <div key={f.label} className="m15-kpi-input-group">
                    <label>{f.label}</label>
                    <input
                      type="number"
                      className="m15-input"
                      value={f.val}
                      step={f.step}
                      onChange={e => f.set(e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="m15-kpi-targets">
              <h4>Year-1 Targets (from Underwriting)</h4>
              <div className="m15-targets-row">
                {[
                  { label: 'Occupancy', val: pct(targetOccupancy), icon: '🏠' },
                  { label: 'Avg Rent',  val: money(targetRent),    icon: '💰' },
                  { label: 'NOI',       val: money(targetNOI),     icon: '📈' },
                  { label: 'Min DSCR',  val: targetDSCR.toFixed(2) + 'x', icon: '🏦' },
                ].map(t => (
                  <div key={t.label} className="m15-target-chip">
                    <span className="m15-target-icon">{t.icon}</span>
                    <div>
                      <div className="m15-target-val">{t.val}</div>
                      <div className="m15-target-label">{t.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* ── MANAGEMENT PLAN ────────────────────────────────── */}
        {activeTab === 'management' && (
          <div className="m15-mgmt-panel">
            <div className="m15-section-header">
              <h3>Property Management Plan</h3>
              <p>Configure your management approach, PM company, and software stack.</p>
            </div>

            <div className="m15-mgmt-toggle">
              {['self_managed', 'third_party'].map(opt => (
                <button
                  key={opt}
                  className={`m15-toggle-btn ${savedMgmt.type === opt ? 'active' : ''}`}
                  onClick={() => updateDealState({ assetManagement: { ...saved, managementPlan: { ...savedMgmt, type: opt } } })}
                >
                  {opt === 'self_managed' ? '🧑 Self-Managed' : '🏢 Third-Party PM'}
                </button>
              ))}
            </div>

            <div className="m15-form-grid">
              <div className="m15-form-group">
                <label>PM Company Name</label>
                <input className="m15-input" value={pmCompany} onChange={e => setPmCompany(e.target.value)} placeholder="e.g. Greystar, Pinnacle, local firm…" />
              </div>
              <div className="m15-form-group">
                <label>Management Fee (%)</label>
                <div className="m15-input-suffix">
                  <input className="m15-input" type="number" value={pmFee} step="0.5" min="0" max="15" onChange={e => setPmFee(e.target.value)} />
                  <span>% of gross revenue</span>
                </div>
              </div>
              <div className="m15-form-group">
                <label>Property Software</label>
                <select className="m15-input" value={pmSoftware} onChange={e => setPmSoftware(e.target.value)}>
                  {PM_SOFTWARE.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="m15-form-group">
                <label>PM Contact / Phone</label>
                <input className="m15-input" value={pmContact} onChange={e => setPmContact(e.target.value)} placeholder="Name · email · phone" />
              </div>
            </div>

            {/* Fee impact */}
            <div className="m15-fee-impact">
              <h4>Management Fee Impact</h4>
              <div className="m15-fee-row">
                <div className="m15-fee-card">
                  <div className="m15-fee-label">Annual Fee Cost</div>
                  <div className="m15-fee-val">{money(revenue * (pmFee / 100))}</div>
                  <div className="m15-fee-note">{pct(pmFee)} × {money(revenue)} gross revenue</div>
                </div>
                <div className="m15-fee-card">
                  <div className="m15-fee-label">Net NOI After PM</div>
                  <div className="m15-fee-val">{money(kpiNOI - revenue * (pmFee / 100))}</div>
                  <div className="m15-fee-note">Baseline NOI minus PM fee</div>
                </div>
                <div className="m15-fee-card">
                  <div className="m15-fee-label">Effective Cap Rate</div>
                  <div className="m15-fee-val">{price > 0 ? pct(((kpiNOI - revenue * (pmFee / 100)) / price) * 100) : '—'}</div>
                  <div className="m15-fee-note">Adjusted for management cost</div>
                </div>
              </div>
            </div>

            {/* Operating procedures */}
            <div className="m15-ops-box">
              <h4>Standard Operating Procedures</h4>
              {[
                { icon: '📋', title: 'Rent Collection',    desc: `Due 1st, late fee after 5th. Online portal via ${pmSoftware}.` },
                { icon: '🔧', title: 'Maintenance',        desc: 'Emergency: 24hr response. Routine: 3 business day SLA.' },
                { icon: '🏠', title: 'Tenant Screening',   desc: '650+ credit score, 3× rent income, background check, references.' },
                { icon: '📊', title: 'Monthly Reporting',  desc: 'P&L, rent roll, maintenance log, vacancy report — delivered by 10th.' },
                { icon: '💰', title: 'Reserve Fund',       desc: '$250/unit/yr capital reserve; separate operating account.' },
              ].map(op => (
                <div key={op.title} className="m15-op-item">
                  <span className="m15-op-icon">{op.icon}</span>
                  <div>
                    <div className="m15-op-title">{op.title}</div>
                    <div className="m15-op-desc">{op.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="m15-form-group full">
              <label>Management Notes</label>
              <textarea className="m15-textarea" rows={3} value={pmNotes} onChange={e => setPmNotes(e.target.value)} placeholder="Lease-up plan, repositioning notes, tenant relations…" />
            </div>
          </div>
        )}

        {/* ── VALUE-ADD PLAN ─────────────────────────────────── */}
        {activeTab === 'valueadd' && (
          <div className="m15-va-panel">
            <div className="m15-section-header">
              <h3>Value-Add Plan</h3>
              <p>Select improvements, set unit counts, and see the projected ROI and NOI uplift.</p>
            </div>

            {/* ROI summary */}
            <div className="m15-va-summary">
              <div className="m15-va-sum-card accent">
                <div className="m15-va-sum-label">Total CapEx</div>
                <div className="m15-va-sum-val">{money(vaTotalCost)}</div>
              </div>
              <div className="m15-va-sum-card">
                <div className="m15-va-sum-label">Rent Bump / Unit</div>
                <div className="m15-va-sum-val">{money(vaRentBump)}/mo</div>
              </div>
              <div className="m15-va-sum-card">
                <div className="m15-va-sum-label">Annual NOI Lift</div>
                <div className="m15-va-sum-val">{money(vaRentBump * units * 12 * 0.85)}</div>
              </div>
              <div className="m15-va-sum-card">
                <div className="m15-va-sum-label">Value-Add ROI</div>
                <div className="m15-va-sum-val">{vaROI.toFixed(1)}%</div>
              </div>
              <div className="m15-va-sum-card">
                <div className="m15-va-sum-label">New Property Value</div>
                <div className="m15-va-sum-val">{money(vaNewValue)}</div>
              </div>
            </div>

            {/* Items */}
            <div className="m15-va-list">
              {VALUE_ADD_ITEMS.map(item => {
                const sel  = vaSelected.includes(item.id)
                const comp = vaCompleted.includes(item.id)
                const n    = Number(vaUnits[item.id] || units)
                return (
                  <div key={item.id} className={`m15-va-item ${sel ? 'selected' : ''} ${comp ? 'completed' : ''}`}>
                    <div className="m15-va-item-top">
                      <label className="m15-va-check">
                        <input type="checkbox" checked={sel} onChange={() => toggleVA(item.id)} />
                        <span className="m15-va-icon">{item.icon}</span>
                        <span className="m15-va-name">{item.label}</span>
                      </label>
                      {sel && (
                        <label className="m15-va-done-check">
                          <input type="checkbox" checked={comp} onChange={() => toggleVaComplete(item.id)} />
                          <span style={{ fontSize: 12, color: comp ? '#1d4ed8' : '#1e40af' }}>{comp ? '✓ Done' : 'Mark done'}</span>
                        </label>
                      )}
                    </div>
                    {sel && (
                      <div className="m15-va-details">
                        <div className="m15-va-detail-row">
                          <span>Units to renovate:</span>
                          <input
                            type="number"
                            className="m15-va-num-input"
                            value={vaUnits[item.id] || units}
                            min={1}
                            max={units}
                            onChange={e => setVaUnit(item.id, e.target.value)}
                          />
                        </div>
                        <div className="m15-va-detail-row">
                          <span>Cost per unit:</span><strong>{money(item.costPer)}</strong>
                        </div>
                        <div className="m15-va-detail-row">
                          <span>Total cost:</span><strong>{money(item.costPer * n)}</strong>
                        </div>
                        <div className="m15-va-detail-row">
                          <span>Rent bump:</span><strong>+{money(item.rentBump)}/mo</strong>
                        </div>
                        <div className="m15-va-detail-row">
                          <span>Annual NOI lift:</span><strong>+{money(item.rentBump * n * 12 * 0.85)}</strong>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ── PERFORMANCE TRACKING ───────────────────────────── */}
        {activeTab === 'performance' && (
          <div className="m15-perf-panel">
            <div className="m15-section-header">
              <h3>Monthly Performance Tracker</h3>
              <p>Enter actuals each month and track against your underwriting targets.</p>
            </div>

            {/* Sparklines */}
            <div className="m15-spark-row">
              {[
                { label: 'Occupancy %', data: perfData.map(r => Number(r.occupancy)), color: '#1d4ed8' },
                { label: 'Avg Rent $',  data: perfData.map(r => Number(r.rent)),      color: '#0284c7' },
                { label: 'Monthly NOI', data: perfData.map(r => Number(r.noi)),        color: '#2563eb' },
              ].map(s => (
                <div key={s.label} className="m15-spark-card">
                  <div className="m15-spark-label">{s.label}</div>
                  <MiniSparkline data={s.data} color={s.color} />
                  <div className="m15-spark-last" style={{ color: s.color }}>
                    {s.label.includes('$') || s.label.includes('NOI') ? money(s.data[s.data.length - 1]) : pct(s.data[s.data.length - 1])}
                  </div>
                </div>
              ))}
            </div>

            {/* Monthly table */}
            <div className="m15-perf-table-wrap">
              <table className="m15-perf-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Occupancy %</th>
                    <th>Avg Rent $</th>
                    <th>Monthly NOI $</th>
                    <th>vs Target</th>
                  </tr>
                </thead>
                <tbody>
                  {perfData.map((row, i) => {
                    const monthlyTarget = Math.round(targetNOI / 12)
                    const diff = Number(row.noi) - monthlyTarget
                    return (
                      <tr key={row.month} className={row.entered ? 'entered' : ''}>
                        <td className="m15-month-cell">{row.month}</td>
                        <td>
                          <input
                            type="number"
                            className="m15-perf-input"
                            value={row.occupancy}
                            step="0.1"
                            onChange={e => updatePerf(i, 'occupancy', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="m15-perf-input"
                            value={row.rent}
                            step="1"
                            onChange={e => updatePerf(i, 'rent', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="m15-perf-input"
                            value={row.noi}
                            step="100"
                            onChange={e => updatePerf(i, 'noi', e.target.value)}
                          />
                        </td>
                        <td>
                          <span className={`m15-diff-badge ${diff >= 0 ? 'above' : 'below'}`}>
                            {diff >= 0 ? '+' : ''}{money(diff)}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="m15-month-cell"><strong>Total</strong></td>
                    <td>{pct(perfData.reduce((s, r) => s + Number(r.occupancy), 0) / 12)}</td>
                    <td>{money(perfData.reduce((s, r) => s + Number(r.rent), 0) / 12)}</td>
                    <td><strong>{money(perfData.reduce((s, r) => s + Number(r.noi), 0))}</strong></td>
                    <td>
                      <span className={`m15-diff-badge ${perfData.reduce((s, r) => s + Number(r.noi), 0) >= targetNOI ? 'above' : 'below'}`}>
                        vs {money(targetNOI)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* ── HOLD STRATEGY ──────────────────────────────────── */}
        {activeTab === 'hold' && (
          <div className="m15-hold-panel">
            <div className="m15-section-header">
              <h3>Hold Strategy &amp; Exit Planning</h3>
              <p>Define your hold period, exit triggers, and see projected returns at disposition.</p>
            </div>

            <div className="m15-hold-toggle">
              {[
                { id: 'sell',      label: '💰 Sell at Exit' },
                { id: 'refinance', label: '🏦 Cash-Out Refi' },
                { id: 'hold_long', label: '⏳ Long-Term Hold' },
              ].map(opt => (
                <button
                  key={opt.id}
                  className={`m15-toggle-btn ${exitStrategy === opt.id ? 'active' : ''}`}
                  onClick={() => setExitStrategy(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="m15-form-grid">
              <div className="m15-form-group">
                <label>Hold Period (Years)</label>
                <input className="m15-input" type="number" value={holdYears} min="1" max="30" onChange={e => setHoldYears(e.target.value)} />
              </div>
              <div className="m15-form-group">
                <label>Exit Cap Rate (%)</label>
                <input className="m15-input" type="number" value={exitCapRate} step="0.1" onChange={e => setExitCapRate(e.target.value)} />
              </div>
              {exitStrategy === 'refinance' && (
                <div className="m15-form-group">
                  <label>Refinance Year</label>
                  <input className="m15-input" type="number" value={refinanceYr} min="1" max={holdYears} onChange={e => setRefinanceYr(e.target.value)} />
                </div>
              )}
            </div>

            {/* Projection cards */}
            <div className="m15-proj-cards">
              {[
                { label: 'Projected Exit Value', val: money(projectedValue), note: `NOI at ${holdYears}yr ÷ ${exitCapRate}% cap`, icon: '🏢' },
                { label: 'Projected Equity',     val: money(projectedEquity),note: 'Value minus est. loan balance',              icon: '💎' },
                { label: 'Projected IRR',         val: pct(projectedIRR),    note: `Levered over ${holdYears} years`,            icon: '📈' },
                { label: 'NOI at Exit',           val: money(projectedNOI),  note: '3% annual rent growth assumed',              icon: '💰' },
              ].map(p => (
                <div key={p.label} className="m15-proj-card">
                  <span className="m15-proj-icon">{p.icon}</span>
                  <div className="m15-proj-val">{p.val}</div>
                  <div className="m15-proj-label">{p.label}</div>
                  <div className="m15-proj-note">{p.note}</div>
                </div>
              ))}
            </div>

            {/* Exit triggers */}
            <div className="m15-triggers">
              <h4>Exit Triggers</h4>
              <div className="m15-trigger-grid">
                {[
                  { icon: '📈', label: 'Value Appreciation',   desc: `Exit if value exceeds ${money(price * 1.4)} (40% gain)` },
                  { icon: '💰', label: 'NOI Threshold',        desc: `Consider exit when NOI reaches ${money(targetNOI * 1.2)}` },
                  { icon: '🏦', label: 'Rate Environment',     desc: 'Sell if refinance rates drop below 5% for cash-out' },
                  { icon: '⚠️', label: 'Market Softening',     desc: 'Monitor cap rate compression — sell before reversal' },
                  { icon: '📅', label: 'Loan Maturity',        desc: 'Plan exit 12 months before balloon payment due' },
                  { icon: '🏘️', label: 'Portfolio Rebalance',  desc: 'Sell if asset exceeds 25% of portfolio by value' },
                ].map(t => (
                  <div key={t.label} className="m15-trigger-card">
                    <span className="m15-trigger-icon">{t.icon}</span>
                    <div className="m15-trigger-label">{t.label}</div>
                    <div className="m15-trigger-desc">{t.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="m15-form-group full">
              <label>Hold Strategy Notes</label>
              <textarea className="m15-textarea" rows={3} value={holdNotes} onChange={e => setHoldNotes(e.target.value)} placeholder="Specific exit conditions, market watch criteria, investor reporting schedule…" />
            </div>

          </div>
        )}

        {/* ── EXPENSE RATIO MONITOR ──────────────────────────── */}
        {activeTab === 'expense' && (() => {
          const modelOpEx  = expItems.reduce((s, e) => s + Number(e.model), 0)
          const actualOpEx = expItems.reduce((s, e) => s + Number(e.actual), 0)
          const modelNOI   = revenue - modelOpEx
          const actualNOI  = revenue - actualOpEx
          const modelOpExPct  = revenue > 0 ? (modelOpEx / revenue) * 100 : 0
          const actualOpExPct = revenue > 0 ? (actualOpEx / revenue) * 100 : 0
          const driftItems = expItems.filter((e) => Number(e.actual) > Number(e.model) * 1.05)
          return (
            <div className="m15-expense-panel">
              <div className="m15-section-header">
                <h3>Expense Ratio Monitor</h3>
                <p>Compare model OpEx assumptions against actual tracked expenses. Drift alerts flag categories exceeding 5% above model.</p>
              </div>
              <div className="m15-exp-summary">
                {[
                  { label: 'Model OpEx %',     val: modelOpExPct.toFixed(1) + '%',  color: '#1d4ed8' },
                  { label: 'Actual OpEx %',    val: actualOpExPct.toFixed(1) + '%', color: actualOpExPct > modelOpExPct * 1.05 ? '#0f2d5e' : '#1d4ed8' },
                  { label: 'Model NOI Margin', val: revenue > 0 ? ((modelNOI / revenue) * 100).toFixed(1) + '%' : '—', color: '#2563eb' },
                  { label: 'Actual NOI Margin',val: revenue > 0 ? ((actualNOI / revenue) * 100).toFixed(1) + '%' : '—', color: actualNOI < modelNOI ? '#0f2d5e' : '#2563eb' },
                ].map((c) => (
                  <div key={c.label} className="m15-exp-sum-card">
                    <div className="m15-exp-sum-val" style={{ color: c.color }}>{c.val}</div>
                    <div className="m15-exp-sum-label">{c.label}</div>
                  </div>
                ))}
              </div>
              {driftItems.length > 0 && (
                <div className="m15-exp-alert">
                  ⚠️ <strong>{driftItems.length} categor{driftItems.length > 1 ? 'ies' : 'y'} exceeding model by &gt;5%:</strong>{' '}
                  {driftItems.map((d) => d.label).join(', ')}
                </div>
              )}
              <div className="m15-exp-gauge-wrap">
                <div className="m15-exp-gauge-label">OpEx Ratio Gauge</div>
                <div className="m15-exp-gauge-track">
                  <div className="m15-exp-gauge-fill model"  style={{ width: `${Math.min(modelOpExPct, 100)}%` }} />
                  <div className="m15-exp-gauge-fill actual" style={{ width: `${Math.min(actualOpExPct, 100)}%`, opacity: 0.65 }} />
                </div>
                <div className="m15-exp-gauge-legend">
                  <span style={{ color: '#1d4ed8' }}>■ Model {modelOpExPct.toFixed(1)}%</span>
                  <span style={{ color: '#0f2d5e' }}>■ Actual {actualOpExPct.toFixed(1)}%</span>
                </div>
              </div>
              <table className="m15-exp-table">
                <thead>
                  <tr><th>Category</th><th>Model ($)</th><th>Actual ($)</th><th>Variance</th><th>% of Rev</th></tr>
                </thead>
                <tbody>
                  {expItems.map((e) => {
                    const variance = Number(e.actual) - Number(e.model)
                    const isDrift  = Number(e.actual) > Number(e.model) * 1.05
                    return (
                      <tr key={e.id} className={isDrift ? 'm15-exp-drift' : ''}>
                        <td>{e.label}</td>
                        <td>${Number(e.model).toLocaleString()}</td>
                        <td>
                          <input type="number" className="m15-exp-input" value={e.actual}
                            onChange={(ev) => setExpItems((prev) => prev.map((x) => x.id === e.id ? { ...x, actual: Number(ev.target.value) } : x))} />
                        </td>
                        <td className={variance > 0 ? 'm15-exp-over' : 'm15-exp-under'}>
                          {variance >= 0 ? '+' : ''}${Math.round(variance).toLocaleString()}
                        </td>
                        <td>{revenue > 0 ? ((Number(e.actual) / revenue) * 100).toFixed(1) + '%' : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )
        })()}

        {/* ── ROI TIMELINE ───────────────────────────────────── */}

      </div>
      <div className="module-bottom-advance">
        <button className={`m15-activate-btn ${saving ? 'loading' : ''}`} onClick={handleActivate} disabled={saving}>
          {saving ? 'Activating Dashboard...' : 'Activate Asset Dashboard & Advance to Module 16'}
        </button>
      </div>
    </div>
  )
}
